import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import { useCollection } from "../../../../firebase-files/hooks/useCollection";
import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import "./PlantationTwoSubscription.scss";
import PGLoaders from "../../../../default-pages/PGLoaders/PGLoaders";

const PlantationTwoSubscription = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("month");
  const [headingColors, setHeadingColors] = useState([
    "color-1",
    "color-2",
    "color-3",
    "color-4",
  ]);
  const [uiConfig, setUiConfig] = useState(null);

  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const [apptype, setAppType] = useState("");

  // Fetch products data
  const { documents: memberships, loading: productsLoading } = useCollection(
    "products",
    ["category", "array-contains", "subscription"]
  );

  // Fetch currency data
  const { document: dbCurrencyDocuments, loading: currencyLoading } =
    useDocument("settings", "currency");

  // Fetch UI configuration
  const { document: uiConfigDoc, loading: uiConfigLoading } = useDocument(
    "uiConfig",
    "subscriptionPage"
  );

  const { document: appTypeDoc } = useDocument("settings", "AppType");


  const { user } = useAuthContext();

  useEffect(() => {
    if (uiConfigDoc) {
      setUiConfig(uiConfigDoc);
      // Update heading colors if configured
      if (uiConfigDoc.headingColors && uiConfigDoc.headingColors.length > 0) {
        setHeadingColors(uiConfigDoc.headingColors);
      }
    }
  }, [uiConfigDoc]);

  // ✅ users collection fetch
    const { documents: users } = useCollection("users");

    // ✅ check if current user is admin or superAdmin
    useEffect(() => {
      if (!user || !users) {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        return;
      }
  
      const phone = user?.phoneNumber?.replace("+91", "91");
      const userInfo = users.find((u) => u.id === phone);
  
      if (userInfo) {
        setIsAdmin(userInfo.role?.toLowerCase() === "admin");
        setIsSuperAdmin(userInfo.role?.toLowerCase() === "superadmin");
      } else {
        setIsAdmin(false);
        setIsSuperAdmin(false);
      }
    }, [user, users]);

      // ✅ load app type from appTypeDoc if available
      useEffect(() => {
        if (appTypeDoc && appTypeDoc.apptype) {
          setAppType(appTypeDoc.apptype);
        }
      }, [appTypeDoc]);

  const handleClick = (productId) => {
    navigate(`/product-details-two/${productId}`);
  };

  const getLabel = (product) => {
    // First check product-specific label
    if (product.customLabel) return product.customLabel.toUpperCase();

    // Then check subCategory
    if (product.subCategory?.[0]) return product.subCategory[0].toUpperCase();

    // Fallback to default from UI config or hardcoded
    return uiConfig?.defaultLabel || "PLAN";
  };

  const renderPrice = (product) => {
    const currencySymbol = dbCurrencyDocuments?.symbol || "$";
    const duration = product.subscriptionDuration || "1";
    const durationType = product.subscriptionDurationType || "month";
    

    if (product.discount && product.price) {
      const discountAmount = Math.ceil(
        (product.price * parseInt(product.discount)) / 100
      );
      const discountedPrice = product.price - discountAmount;

      return (
        <>
          <del style={{ color: uiConfig?.originalPriceColor || "red" }}>
            {currencySymbol}
            {product.price}
          </del>{" "}
          &nbsp;
          <strong
            style={{ color: uiConfig?.discountedPriceColor || "inherit" }}
          >
            {currencySymbol}
            {discountedPrice}
            {parseInt(duration) > 1 && `/${duration} ${durationType}`}
            {parseInt(duration) === 1 && `/${durationType}`}
          </strong>
        </>
      );
    }

    return (
      <>
        {currencySymbol}
        {product.price || "--"}
        {parseInt(duration) > 1 && `/${duration} ${durationType}`}
        {parseInt(duration) === 1 && `/${durationType}`}
      </>
    );
  };

  const renderBadge = (product) => {
    if (!product.popularProducts && !product.discount) return null;

    const badgeStyle = {
      backgroundColor: uiConfig?.badgeBackgroundColor || "#ff5722",
      color: uiConfig?.badgeTextColor || "#fff",
    };

    return (
      <div className="subscription-discount-badge" style={badgeStyle}>
        {product.popularProducts
          ? uiConfig?.popularLabel || "Popular"
          : `${uiConfig?.saveLabel || "Save"} ${product.discount}`}
      </div>
    );
  };

  const renderCard = (product, index) => {
    const colorClass = headingColors[index % headingColors.length];
    const label = getLabel(product);

    return (
      <div
        className="card"
        key={product.id}
        onClick={() => handleClick(product.id)}
        style={uiConfig?.cardStyle || {}}
      >
        <h3
          className={`card-heading ${colorClass}`}
          style={uiConfig?.headingStyle || {}}
        >
          <span>{product.name}</span>
        </h3>

        {renderBadge(product)}

        <ul className="card-features" style={uiConfig?.featuresListStyle || {}}>
          
          {(product.short_description || "")
          .split("\n")
           .filter(line => line.trim() !== "")
          .map((line, idx) => (
            <li
              key={idx}
              className={line.includes("[x]") ? "inactive" : ""}
              style={
                line.includes("[x]")
                  ? uiConfig?.inactiveFeatureStyle || {}
                  : uiConfig?.activeFeatureStyle || {}
              }
            >
              {line.replace("[x]", "").trim()}
            </li>
          ))}
        </ul>

        <p className="price" style={uiConfig?.priceStyle || {}}>
          {renderPrice(product)}
        </p>

        <button className="card-button" style={uiConfig?.buttonStyle || {}}>
          {product.subscriptionFreeTrail > 0
            ? `${uiConfig?.freeTrialPrefix || "Start"} ${
                product.subscriptionFreeTrail
              }-${uiConfig?.freeTrialSuffix || "days Free Trial"}`
            : label === "GOLD MEMBER"
            ? uiConfig?.exploreLabel || "Explore"
            : uiConfig?.buyNowLabel || "Buy Now"}
        </button>
      </div>
    );
  };

  const availableBillingCycles = Array.from(
    new Set(
      memberships
        ?.filter((product) => product.status?.toLowerCase() === "active")
        .map((product) =>
          (product.subscriptionDurationType || "month").toLowerCase()
        )
    )
  );

  // Only show toggle buttons if there are 2 or more different billing cycles
  const showToggleButtons = availableBillingCycles.length > 1;

  const activeMemberships = memberships
    ?.filter((product) => product.status?.toLowerCase() === "active")
    ?.filter((product) =>
      product.subscriptionDurationType
        ? product.subscriptionDurationType.toLowerCase() === billingCycle
        : billingCycle === "month"
    );

  if (productsLoading || currencyLoading || uiConfigLoading) {
    return (
      <div className="loading-container" style={uiConfig?.loadingStyle || {}}>
        <PGLoaders />
      </div>
    );
  }

    const showAddSubscriptionButton = () => {
    if (apptype === "lite" && isSuperAdmin) return true;
    if (apptype === "pro" && (isSuperAdmin || isAdmin)) return true;
    return false;
  };

  return (
    <section
      className="plantationTwo-Subscription"
      style={uiConfig?.containerStyle || {}}
    >
      <h2 className="section-title" style={uiConfig?.titleStyle || {}}>
        {uiConfig?.sectionTitle || "Subscription Plans"}
      </h2>

      {/* Toggle Buttons - Only show if there are 2 or more billing cycles */}
      {showToggleButtons && (
        <div
          className="subscription-toggle-btns"
          style={uiConfig?.toggleContainerStyle || {}}
        >
          {availableBillingCycles.map((cycle) => (
            <button
              key={cycle}
              className={`toggle-btn ${billingCycle === cycle ? "active" : ""}`}
              onClick={() => setBillingCycle(cycle)}
              style={{
                ...(uiConfig?.toggleButtonStyle || {}),
                ...(billingCycle === cycle
                  ? uiConfig?.activeToggleButtonStyle || {}
                  : {}),
              }}
            >
              {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
            </button>
          ))}
        </div>
      )}

      <div
        className="carousel-wrapper-subscription"
        style={uiConfig?.carouselWrapperStyle || {}}
      >
        <div className="carousel" style={uiConfig?.carouselStyle || {}}>
          {activeMemberships?.length > 0 ? (
            activeMemberships.map(renderCard)
          ) : (
            <p style={uiConfig?.noPlansStyle || {}}>
              {uiConfig?.noPlansText || "No plans available at the moment."}
            </p>
          )}
        </div>
      </div>

      {/*Add button on the basis of lite and pro */}
      {showAddSubscriptionButton() && (
        <div
          className="add-subscription-btn-wrapper"
          style={uiConfig?.adminButtonWrapperStyle || {}}
        >
          <button
            className="add-subscription-btn"
            onClick={() =>
              navigate(uiConfig?.adminAddRoute || "/subscription-details")
            }
            style={uiConfig?.adminButtonStyle || {}}
          >
            {uiConfig?.adminButtonText || "Add New"}
          </button>
        </div>
      )}
    </section>
  );
};

export default PlantationTwoSubscription;
